import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Icon from 'components/Utils/NewIcon';
import { DefaultDivider } from 'services/UIUtils';
import moment from 'moment-timezone';
import PhysicianBadge from 'components/Physician/PhysicianBadge';
import DefaultTextField from 'components/Utils/DefaultTextField';
import './style.scss';
import AppointmentUtils from 'services/AppointmentUtils';
import EditAppointment from 'components/HealthRecords/Appointments/EditAppointment';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Schedule from 'components/Schedule';
import CalendarUtils from 'services/CalendarUtils';
import AppointmentRating from 'components/HealthRecords/Appointments/AppointmentRating/index';
import ResponsiveColumns from 'components/ResponsiveColumns';

const dateFormat = 'Do MMM YYYY';
const timeFormat = 'h:mm A';

function AppointmentDetails(props) {
  const { physicians, appointment, hideCalendar, showCalendar } = props;

  const [editMode, setEditMode] = useState(
    props.allowEdit !== undefined ? props.allowEdit && props.role === 'admin' : false,
  );

  const toggleEditMode = (val) => {
    setEditMode(val);
  };

  const handleEditClose = (updatedAppointment) => {
    // EditAppointment passes the updated appointment on save and nothing on
    // reset/cancel. AppointmentUtils.getEditCloseAction turns that (plus this
    // surface's allowEdit/role) into the routing decision — kept pure so it can
    // be unit-tested without a React harness.
    const action = AppointmentUtils.getEditCloseAction({
      allowEdit: props.allowEdit,
      role: props.role,
      savedAppointment: updatedAppointment,
    });

    // Admin auto-edit surface, just saved: keep the editor open showing the
    // saved values so the screen stays the same as before (the reported
    // expectation). EditAppointment already re-seeds its form from the response,
    // so there is nothing to do here. Falling back to the read-only branch would
    // show the patient-facing rating stars and a non-practitioner schedule —
    // exactly the wrong view reported when an admin saves.
    if (action === 'stay') return;

    // Admin auto-edit surface, reset/cancelled: there is no read-only view to
    // land on, so close the whole surface (modal / detail pane). Guarded so it
    // degrades safely if a future caller mounts this with admin auto-edit but no
    // onClose (it simply stays in the editor).
    if (action === 'closeSurface') {
      if (props.onClose) props.onClose();
      return;
    }

    // Read-only origin (a physician opened the details then clicked Edit):
    // keep the existing behaviour and return to the read-only details view.
    toggleEditMode(false);
  };

  const getTopBar = () => {
    let { allowClose, onClose } = props;

    if (!allowClose) return null;

    return (
      <div className="top-bar">
        <div>
          <span className="small-title">Appointment Details</span>
          <div className="close">
            <Icon src="xmark" onClick={onClose} />
          </div>
        </div>
      </div>
    );
  };

  const getEditPanel = () => {
    if (!props.allowEdit) return null;

    return (
      <div className="row" style={{ marginTop: '10px' }}>
        <DefaultButton
          name="Edit"
          onClick={() => {
            toggleEditMode(true);
          }}
          style={{ width: '145px', marginRight: '20px' }}
        />
      </div>
    );
  };

  if (editMode)
    return (
      <EditAppointment
        hideCalendar={hideCalendar}
        appointment={appointment}
        defaultShowCalendar={showCalendar}
        onClose={(updatedAppointment) => handleEditClose(updatedAppointment)}
      />
    );

  let day = CalendarUtils.format(appointment.date, dateFormat);
  let time = CalendarUtils.format(appointment.date, timeFormat);

  let physician = physicians[appointment.physician_id];
  let physicianContent = <PhysicianBadge physician={physician} />;

  let topBar = getTopBar();

  const detailsContent = (
    <div className="appointment-view padded-box scroll-list">
      {topBar}
      <div className="data-content">
        <DefaultTextField
          className="type"
          name="type"
          label="Appointment Type"
          style={{ width: 'calc(50% - 10px)', marginRight: '20px' }}
          value={appointment.type}
          disabled={true}
        />
        {AppointmentUtils.isTelemedicineAppointment(appointment) && (
          <DefaultTextField
            name="telemedicine_method"
            label="Telemedicine Method"
            style={{ width: 'calc(50% - 10px)' }}
            value={appointment.telemedicine_method}
            disabled={true}
          />
        )}
        <DefaultTextField
          name="status"
          label="Status"
          style={{ width: 'calc(100% - 10px)' }}
          value={appointment.status}
          disabled={true}
        />
        <DefaultTextField
          name="day"
          label="Day"
          style={{ width: 'calc(50% - 10px)', marginRight: '20px' }}
          value={day}
          disabled={true}
        />
        <DefaultTextField
          name="time"
          label="Time"
          style={{ width: 'calc(50% - 10px)' }}
          value={time}
          disabled={true}
        />
        <span className="wide-label">Practitioner</span>
        {physicianContent}
        {!!appointment.concern_text && (
          <>
            <span className="small-title">Concern</span>
            <pre>{appointment.concern_text}</pre>
          </>
        )}
        {!!appointment.notes && (
          <>
            <span className="small-title">Notes</span>
            <pre>{appointment.notes}</pre>
          </>
        )}
        {!!appointment.complaints && (
          <>
            <span className="small-title">Complaints</span>
            <pre>{appointment.complaints}</pre>
          </>
        )}
        {props.role === 'admin' && (
          <div className="row">
            <div style={{ width: '100%', marginTop: '17px' }}>
              <span className="floating-label">Rating</span>
              <AppointmentRating appointment={appointment} />
              <DefaultDivider style={{ width: '100%' }} />
            </div>
          </div>
        )}
        {getEditPanel()}
      </div>
    </div>
  );

  if (hideCalendar)
    return <div className="details-panel appointment-details">{detailsContent}</div>;

  return (
    <div className="details-panel appointment-details show-calendar">
      <ResponsiveColumns
        defaultTab={showCalendar ? 1 : 0}
        columns={[
          {
            name: 'Details',
            element: detailsContent,
          },
          {
            name: 'Calendar',
            element: (
              <div className="calendar-view scroll-list">
                <Schedule
                  fetchShifts={true}
                  showCalendar={false}
                  showDay={appointment.date || moment().unix()}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function mapStateToProps(state) {
  return {
    physicians: state.physicians.byId,
    role: state.authentication.role,
  };
}

AppointmentDetails.propTypes = {
  appointment: PropTypes.object,
  onClose: PropTypes.func,
  allowClose: PropTypes.bool,
  allowEdit: PropTypes.bool,
  hideCalendar: PropTypes.bool,
};

export default connect(mapStateToProps)(AppointmentDetails);
